export type WebcamFacingMode = "user" | "environment";

const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = DEFAULT_WIDTH * (3 / 4);

export class WebcamUtils {
    private _webcamElement: HTMLVideoElement;
    private _facingMode: WebcamFacingMode;
    private _webcamList: MediaDeviceInfo[] = [];
    private _streamList: MediaStream[] = [];
    private _selectedDeviceId: string = "";
    private _canvasElement: HTMLCanvasElement | null;
    private _context: CanvasRenderingContext2D | null = null;

    /**
     * Vrátí facingMode
     */
    public get facingMode(): WebcamFacingMode {
        return this._facingMode;
    }

    public set facingMode(value) {
        this._facingMode = value;
    }

    /**
     * Vrací seznam vstupních zařízení
     */
    public get webcamList(): readonly MediaDeviceInfo[] {
        return Array.from(this._webcamList);
    }

    /**
     * Vrací počet vstupních zařízení
     */
    public get webcamCount(): number {
        return this._webcamList.length;
    }

    /**
     * Vrací ID právě vybraného zařízení
     */
    public get selectedDeviceId(): string {
        return this._selectedDeviceId;
    }

    constructor(webcamElement: HTMLVideoElement, facingMode: WebcamFacingMode = "user", canvasElement: HTMLCanvasElement | null = null) {
        this._webcamElement = webcamElement;
        this._webcamElement.width = this._webcamElement.width || DEFAULT_WIDTH;
        this._webcamElement.height = this._webcamElement.height || DEFAULT_HEIGHT;
        this._facingMode = facingMode;
        this._canvasElement = canvasElement;

        if (canvasElement) {
            this._context = canvasElement.getContext("2d");
        }
    }

    /**
     * Vrátí seznam všech video vstupů
     */
    public getVideoInputs(mediaDevices: MediaDeviceInfo[]): MediaDeviceInfo[] {
        this._webcamList = WebcamUtils.getVideoInputs(mediaDevices);
        if (this._webcamList.length === 1) {
            this._facingMode = "user";
        }
        return this._webcamList;
    }

    public static getVideoInputs(mediaDevices: MediaDeviceInfo[]): MediaDeviceInfo[] {
        const webcamList: MediaDeviceInfo[] = [];
        mediaDevices.forEach(mediaDevice => {
            if (mediaDevice.kind === "videoinput") {
                webcamList.push(mediaDevice);
            }
        });

        return webcamList;
    }

    /**
     * Vrátí nastavení oprávnění přístupu
     */
    public getMediaConstraints(): MediaStreamConstraints {
        const videoConstraints: MediaTrackConstraints = {};
        if (this._selectedDeviceId === "") {
            videoConstraints.facingMode = this._facingMode;
        } else {
            videoConstraints.deviceId = { exact: this._selectedDeviceId };
        }

        return {
            video: videoConstraints,
            audio: false,
        };
    }

    /**
     * Výběr kamery na základě facingMode
     */
    public selectCamera(): void {
        for (const webcam of this._webcamList) {
            if ((this._facingMode === "user" && webcam.label.toLowerCase().includes("front"))
                || (this._facingMode === "environment" && webcam.label.toLowerCase().includes("back"))
            ) {
                this._selectedDeviceId = webcam.deviceId;
                break;
            }
        }
    }

    /**
     * Změní facingMode a vybere kameru
     */
    public flip(): void {
        this._facingMode = (this._facingMode === "user") ? "environment" : "user";
        this._webcamElement.style.transform = "";
        this.selectCamera();
    }

    /**
     * Spustí stream (Načte všechny kamery, vyžádá práva a vybere kameru na základě facingMode)
     */
    public async start(startStream = true): Promise<string> {
        this.stop();
        const stream = await navigator.mediaDevices.getUserMedia(this.getMediaConstraints());
        this._streamList.push(stream);
        await this.info();

        this.selectCamera();
        if (startStream) {
            await this.stream();
            this._timerCallback();
        } else {
            return this._selectedDeviceId;
        }

        return this._selectedDeviceId;
    }

    /**
     * Vrátí všechny video vstupy
     */
    public async info(): Promise<readonly MediaDeviceInfo[]> {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return this.getVideoInputs(devices);
    }

    public static async info(): Promise<readonly MediaDeviceInfo[]> {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return this.getVideoInputs(devices);
    }

    /**
     * Zahájí stream videa do videoElement
     */
    public async stream(): Promise<WebcamFacingMode> {
        const stream = await navigator.mediaDevices.getUserMedia(this.getMediaConstraints());
        this._streamList.push(stream);
        this._webcamElement.srcObject = stream;
        if (this._facingMode === "user") {
            this._webcamElement.style.transform = "scale(-1,1)";
        }
        this._webcamElement.play();

        return this._facingMode;
    }

    /**
     * Zastaví stream
     */
    public stop(): void {
        this._streamList.forEach(stream => {
            stream.getTracks().forEach(track => {
                track.stop();
            });
        });
    }

    private _videoStreamToCanvas(): void {
        this._context?.drawImage(this._webcamElement, 0, 0, this._webcamElement.videoWidth, this._webcamElement.videoHeight);
    }

    private _timerCallback(): void {
        if (this._webcamElement.paused || this._webcamElement.ended) return;

        this._videoStreamToCanvas();

        self.setTimeout(() => this._timerCallback(), 0);
    }

    private _snapPrepare(): void {
        if (this._canvasElement != null) {
            this._canvasElement.height = this._webcamElement.scrollHeight;
            this._canvasElement.width = this._webcamElement.scrollWidth;

            if (!this._context) this._context = this._canvasElement.getContext("2d");
            if (!this._context) throw "There was an error in creating the context";

            if (this._facingMode === "user") {
                this._context.translate(this._canvasElement.width, 0);
                this._context.scale(-1, 1);
            }
            this._context.clearRect(0, 0, this._canvasElement.width, this._canvasElement.height);
            this._context.drawImage(this._webcamElement, 0, 0, this._canvasElement.width, this._canvasElement.height);
        }
    }

    /**
     * Pořídí snímek z webkamery
     *
     * @return Vrátí snímek v Base64 (image/jpeg)
     */
    public snapToDataUrl(): string {
        this._snapPrepare();
        if (this._canvasElement) {
            return this._canvasElement.toDataURL("image/jpeg");
        } else {
            throw "canvas element is missing";
        }
    }

    /**
     * Pořídí snímek z webkamery
     *
     * @return Vrátí snímek v blob (image/jpeg)
     */
    public snapToBlob(): Promise<Blob | null> {
        return new Promise((resolve, reject) => {
            this._snapPrepare();
            if (this._canvasElement) {
                this._canvasElement.toBlob((blob) => {
                    resolve(blob);
                }, "image/jpeg");
            } else {
                reject("canvas element is missing");
            }
        });
    }
}
